import {
  DEFAULT_PRESENTATION,
  FORMAT_RULES,
  interactionSchema,
  type Format,
  type InteractionContent,
  type NewsletterSignup,
  type Option,
  type Presentation,
  type Question,
  type QuestionType,
} from '@quizee/core';
import { newId } from '@quizee/events';
import { resolvedSignupConsent, signupWouldVanish } from './newsletter';

/**
 * The editing shape of an interaction, and the one way to turn it into interaction content.
 *
 * There is one draft for every format, not one per format, because there is one content model.
 * What changes between a poll and a personality quiz is which parts of this draft the screen shows
 * and which constraints the schema then applies, and both of those come from FORMAT_RULES.
 *
 * The form holds loose strings while somebody is typing. Saving builds the real content object and
 * runs interactionSchema over it, the same schema the API runs, so the editor refuses exactly what
 * the server would refuse. Zod messages are written for developers, so they are translated into
 * sentences here before anyone sees them.
 *
 * Ids are the thing to be careful with. A question id and an option id are what published results
 * are keyed by, so an id that gets regenerated silently detaches every answer already counted
 * against it. Every draft row therefore carries the id it came from, and a separate `key` for
 * React and for input ids, so rows can be reordered, retyped and re-rendered without the id ever
 * moving.
 */

export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 10;

/** The widest a scale question is allowed to run, so a reader is not asked to pick out of 200. */
export const MAX_SCALE = 100;

/**
 * How many outcomes a personality quiz can offer.
 *
 * The schema allows thirty. Eight is what a person can point an answer at without a scrollbar, and
 * a quiz with more results than that has stopped being readable long before it stops validating.
 */
export const MIN_OUTCOMES = 2;
export const MAX_OUTCOMES = 8;

export interface OptionDraft {
  /** Stable key for React and for input ids. Not the id, and never sent to the server. */
  readonly key: string;
  /** The id of an existing option, kept so published results stay attached to it. */
  readonly id: string | null;
  readonly label: string;
  /** Trivia only. */
  readonly isCorrect: boolean;
  /**
   * Personality only: how hard this answer pushes towards each outcome, keyed by the outcome's
   * draft key rather than its id, because an outcome typed a moment ago has no id yet.
   */
  readonly weights: Readonly<Record<string, number>>;
}

export interface ScaleDraft {
  readonly min: string;
  readonly max: string;
  readonly minLabel: string;
  readonly maxLabel: string;
}

export interface QuestionDraft {
  readonly key: string;
  readonly id: string | null;
  readonly type: QuestionType;
  readonly prompt: string;
  /** Free text questions only. */
  readonly placeholder: string;
  /**
   * Free text questions only: true for a paragraph, false for one line, null for neither.
   *
   * Null is a real answer, not a missing one. It means nobody has chosen and the widget should go
   * on guessing from the format, which is what every document published before the field existed
   * says. Writing false instead would quietly take that choice away.
   */
  readonly multiline: boolean | null;
  /** Scale questions only. Kept while another type is selected, so switching back is free. */
  readonly scale: ScaleDraft;
  readonly options: readonly OptionDraft[];
}

export interface OutcomeDraft {
  readonly key: string;
  readonly id: string | null;
  readonly title: string;
  readonly description: string;
  readonly imageUrl: string;
}

/**
 * The newsletter signup step, as the editor holds it.
 *
 * `enabled` is the whole feature from a publisher's point of view: whether readers who finish this
 * interaction are offered the newsletter. The two labels are optional decoration over the widget's
 * own wording.
 *
 * `consentText` is the one field with three states packed into two, and the packing is deliberate.
 * Null means this interaction writes no wording of its own and takes the masthead's, which is what
 * almost every interaction should do: the sentence a reader agrees to is a legal statement about a
 * list, and a list belongs to a masthead. A string is an override for the one off case, a campaign
 * signup that promises something this particular quiz is about. There is no third "show nothing"
 * choice here, because `enabled` already says that and saying it twice would let the two disagree.
 *
 * `listId` is null on exactly the same terms: absent means the brand's default list, which is what
 * every signup published before lists existed already meant, so nothing has to change under it.
 * A string names one of the brand's other lists on purpose, for a campaign that should not join
 * the everyday briefing. Null must never be quietly filled in with whichever list happens to be
 * default today, because the two stop meaning the same thing the day somebody changes which list
 * that is.
 */
export interface SignupDraft {
  readonly enabled: boolean;
  readonly heading: string;
  readonly buttonLabel: string;
  readonly consentText: string | null;
  readonly listId: string | null;
}

export const SIGNUP_HEADING_MAX = 200;
export const SIGNUP_BUTTON_MAX = 60;

export interface InteractionDraft {
  readonly format: Format;
  readonly title: string;
  readonly introduction: string;
  readonly completionMessage: string;
  readonly questions: readonly QuestionDraft[];
  /** Personality only, and empty everywhere else. */
  readonly outcomes: readonly OutcomeDraft[];
  readonly signup: SignupDraft;
}

/** No signup, and no wording waiting behind it. What every interaction starts as. */
export const NO_SIGNUP: SignupDraft = {
  enabled: false,
  heading: '',
  buttonLabel: '',
  consentText: null,
  listId: null,
};

let keyCounter = 0;

function nextKey(prefix: string): string {
  keyCounter += 1;
  return `${prefix}-${String(keyCounter)}`;
}

export function newOptionDraft(label = ''): OptionDraft {
  return { key: nextKey('opt'), id: null, label, isCorrect: false, weights: {} };
}

export const DEFAULT_SCALE: ScaleDraft = { min: '1', max: '5', minLabel: '', maxLabel: '' };

export function newQuestionDraft(type: QuestionType, format?: Format): QuestionDraft {
  const choice = isChoice(type);
  return {
    key: nextKey('que'),
    id: null,
    type,
    prompt: '',
    placeholder: '',
    multiline: format === undefined ? null : defaultMultiline(format, type),
    scale: DEFAULT_SCALE,
    options: choice ? [newOptionDraft(), newOptionDraft()] : [],
  };
}

/**
 * How much room a new free text question starts with.
 *
 * These are the guesses the widget already makes for a question that carries no flag of its own
 * (`longText` in its format behaviour table): a survey asks people to tell you something, a form
 * asks for a name and a postcode. Starting on the guess means the control shows what would have
 * happened anyway, so filling it in changes nothing for a reader until somebody changes their mind.
 */
function defaultMultiline(format: Format, type: QuestionType): boolean | null {
  if (type !== 'free_text') return null;
  if (format === 'survey') return true;
  if (format === 'form') return false;
  return null;
}

export function newOutcomeDraft(): OutcomeDraft {
  return { key: nextKey('out'), id: null, title: '', description: '', imageUrl: '' };
}

export function isChoice(type: QuestionType): boolean {
  return type === 'single_choice' || type === 'multiple_choice';
}

/** A blank interaction of the given format, already shaped the way that format needs. */
export function emptyDraft(format: Format): InteractionDraft {
  const rules = FORMAT_RULES[format];
  const firstType = rules.questionTypes[0] ?? 'single_choice';
  return {
    format,
    title: '',
    introduction: '',
    completionMessage: '',
    questions: [newQuestionDraft(firstType, format)],
    // Two, because a personality quiz with one outcome has nothing to choose between.
    outcomes: rules.requiresOutcomes ? [newOutcomeDraft(), newOutcomeDraft()] : [],
    signup: NO_SIGNUP,
  };
}

/** Read a saved interaction back into a draft, keeping every id exactly as it was. */
export function draftFromContent(content: InteractionContent): InteractionDraft {
  const rules = FORMAT_RULES[content.format];

  const outcomes = (content.outcomes ?? []).map((outcome) => ({
    key: nextKey('out'),
    id: outcome.id,
    title: outcome.title,
    description: outcome.description ?? '',
    imageUrl: outcome.image_url ?? '',
  }));
  const keyByOutcomeId = new Map(outcomes.map((outcome) => [outcome.id, outcome.key]));

  const questions = content.questions.map((question) => ({
    key: nextKey('que'),
    id: question.id,
    type: question.type,
    prompt: question.prompt,
    placeholder: question.placeholder ?? '',
    multiline: question.multiline ?? null,
    scale: {
      min: question.scale === undefined ? DEFAULT_SCALE.min : String(question.scale.min),
      max: question.scale === undefined ? DEFAULT_SCALE.max : String(question.scale.max),
      minLabel: question.scale?.min_label ?? '',
      maxLabel: question.scale?.max_label ?? '',
    },
    options: question.options.map((option) => ({
      key: nextKey('opt'),
      id: option.id,
      label: option.label,
      isCorrect: option.is_correct === true,
      weights: weightsByKey(option.outcome_weights, keyByOutcomeId),
    })),
  }));

  return {
    format: content.format,
    title: content.title,
    introduction: content.introduction ?? '',
    completionMessage: content.completion_message ?? '',
    questions:
      questions.length > 0
        ? questions
        : [newQuestionDraft(rules.questionTypes[0] ?? 'single_choice', content.format)],
    outcomes,
    signup: signupDraft(content.signup),
  };
}

/**
 * A saved signup block as the form holds it.
 *
 * Absent means no step, which is what every interaction published before the step existed carries.
 * An absent `consent_text` means the wording is the masthead's, and null is how the form says so.
 * An absent `list_id` means the same thing about the list: every signup saved before lists existed
 * carries none, and that has always meant the brand's default list.
 */
export function signupDraft(signup: NewsletterSignup | undefined): SignupDraft {
  if (signup === undefined) return NO_SIGNUP;
  return {
    enabled: signup.enabled,
    heading: signup.heading ?? '',
    buttonLabel: signup.button_label ?? '',
    consentText: signup.consent_text ?? null,
    listId: signup.list_id ?? null,
  };
}

function weightsByKey(
  weights: Readonly<Record<string, number>> | undefined,
  keyByOutcomeId: ReadonlyMap<string, string>,
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [outcomeId, weight] of Object.entries(weights ?? {})) {
    const key = keyByOutcomeId.get(outcomeId);
    // A weight pointing at an outcome that is no longer declared is dropped rather than carried
    // forward: the schema rejects it, and the editor has no way to show what it referred to.
    if (key !== undefined) result[key] = weight;
  }
  return result;
}

export type BuildResult =
  | { readonly ok: true; readonly content: InteractionContent }
  | { readonly ok: false; readonly errors: readonly string[] };

function trimmed(value: string): string {
  return value.trim();
}

function wholeNumber(value: string): number {
  const text = value.trim();
  // Number('') is 0, which would quietly accept an empty box as a zero.
  return text === '' ? Number.NaN : Number(text);
}

/**
 * Build interaction content from a draft.
 *
 * Blank option rows are dropped, so clearing an option's text is the same as removing it and the
 * schema then says a choice question needs at least two options. Existing ids are kept, so editing
 * the words of an option does not orphan the answers already counted against it.
 *
 * Everything a format does not use is left out rather than written as an empty value, because the
 * schema rejects a poll that carries outcomes or a survey that carries correct answers.
 *
 * `inheritedNewsletterConsent` is the masthead's wording, and it is here rather than on the screen
 * because a signup with no wording is refused rather than warned about. Pass it as undefined when
 * the brand could not be read: an unknown wording is not an empty one, and refusing a save over a
 * value nobody could look up would be worse than letting publishing drop the step.
 */
export function buildInteractionContent(
  draft: InteractionDraft,
  base?: InteractionContent,
  inheritedNewsletterConsent?: string,
): BuildResult {
  const rules = FORMAT_RULES[draft.format];
  const single = rules.maxQuestions === 1;
  const early: string[] = [];

  const signup = buildSignup(draft.signup);
  if (signup?.enabled === true && inheritedNewsletterConsent !== undefined) {
    const wording = resolvedSignupConsent(draft.signup.consentText, inheritedNewsletterConsent);
    if (signupWouldVanish(wording)) {
      early.push(
        'The newsletter signup has no wording to show readers beside the tick box, so publishing would leave it out altogether. Write the wording here, or set one for the brand.',
      );
    }
  }

  const baseQuestions = new Map<string, Question>();
  const baseOptions = new Map<string, Option>();
  for (const question of base?.questions ?? []) {
    baseQuestions.set(question.id, question);
    for (const option of question.options) baseOptions.set(option.id, option);
  }

  const outcomeIdByKey = new Map<string, string>();
  const outcomes = draft.outcomes.map((outcome) => {
    const id = outcome.id ?? newId('outcome');
    outcomeIdByKey.set(outcome.key, id);
    const description = trimmed(outcome.description);
    const imageUrl = trimmed(outcome.imageUrl);
    return {
      id,
      title: trimmed(outcome.title),
      ...(description === '' ? {} : { description }),
      ...(imageUrl === '' ? {} : { image_url: imageUrl }),
    };
  });

  const questions = draft.questions.map((question, index) => {
    const existing = question.id === null ? undefined : baseQuestions.get(question.id);
    const choice = isChoice(question.type);
    const labelled = choice
      ? question.options.filter((option) => trimmed(option.label) !== '')
      : [];

    if (labelled.length > MAX_OPTIONS) {
      early.push(
        single
          ? `A ${rules.label.toLowerCase()} takes at most ${String(MAX_OPTIONS)} options.`
          : `Question ${String(index + 1)} takes at most ${String(MAX_OPTIONS)} options.`,
      );
    }

    const options = labelled.map((option) => {
      const existingOption = option.id === null ? undefined : baseOptions.get(option.id);
      const weights: Record<string, number> = {};
      if (rules.requiresOutcomes) {
        for (const [outcomeKey, weight] of Object.entries(option.weights)) {
          const outcomeId = outcomeIdByKey.get(outcomeKey);
          if (outcomeId !== undefined) weights[outcomeId] = weight;
        }
      }
      return {
        id: option.id ?? newId('option'),
        label: trimmed(option.label),
        // Images on options are not editable yet, so an existing one is carried through rather
        // than dropped by a save from a screen that never showed it.
        ...(existingOption?.image_url === undefined ? {} : { image_url: existingOption.image_url }),
        ...(rules.requiresCorrectAnswers && option.isCorrect ? { is_correct: true } : {}),
        ...(Object.keys(weights).length === 0 ? {} : { outcome_weights: weights }),
      };
    });

    const placeholder = trimmed(question.placeholder);
    const minLabel = trimmed(question.scale.minLabel);
    const maxLabel = trimmed(question.scale.maxLabel);
    const scaleMin = wholeNumber(question.scale.min);
    const scaleMax = wholeNumber(question.scale.max);

    // The schema has no opinion on how wide a scale is, because a scale of 1 to 500 is valid
    // content. It is not a thing a reader can tap, so the editor holds the line instead.
    if (
      question.type === 'scale' &&
      Number.isInteger(scaleMin) &&
      Number.isInteger(scaleMax) &&
      scaleMax - scaleMin > MAX_SCALE
    ) {
      early.push(
        single
          ? `A scale can run over at most ${String(MAX_SCALE)} numbers.`
          : `Question ${String(index + 1)}: a scale can run over at most ${String(MAX_SCALE)} numbers.`,
      );
    }

    return {
      id: question.id ?? newId('question'),
      type: question.type,
      prompt: trimmed(question.prompt),
      // Not editable yet. Carried through for the same reason as the option image above.
      required: existing?.required ?? true,
      options,
      ...(existing?.image_url === undefined ? {} : { image_url: existing.image_url }),
      ...(question.type === 'free_text' && placeholder !== '' ? { placeholder } : {}),
      ...(question.type === 'free_text' && question.multiline !== null
        ? { multiline: question.multiline }
        : {}),
      ...(question.type === 'scale'
        ? {
            scale: {
              min: scaleMin,
              max: scaleMax,
              ...(minLabel === '' ? {} : { min_label: minLabel }),
              ...(maxLabel === '' ? {} : { max_label: maxLabel }),
            },
          }
        : {}),
    };
  });

  const presentation: Presentation = {
    ...(base?.presentation ?? DEFAULT_PRESENTATION),
    // A survey and a form have no reader facing results, and the schema says so.
    ...(rules.hasResults ? {} : { show_results: 'never' as const }),
  };

  const introduction = trimmed(draft.introduction);
  const completionMessage = trimmed(draft.completionMessage);

  const content = {
    format: draft.format,
    title: trimmed(draft.title),
    ...(introduction === '' ? {} : { introduction }),
    questions,
    ...(rules.requiresOutcomes ? { outcomes } : {}),
    ...(completionMessage === '' ? {} : { completion_message: completionMessage }),
    presentation,
    ...(signup === undefined ? {} : { signup }),
  };

  const parsed = interactionSchema.safeParse(content);
  if (parsed.success && early.length === 0) return { ok: true, content: parsed.data };

  const errors: string[] = [...early];
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const text = friendlyMessage(draft.format, issue.path, issue.message);
      if (!errors.includes(text)) errors.push(text);
    }
  }
  if (errors.length === 0) {
    errors.push(`This ${rules.label.toLowerCase()} is not ready to save yet.`);
  }
  return { ok: false, errors };
}

/**
 * The signup block to save, or nothing at all.
 *
 * Nothing is the answer for an interaction that has never been near the step, which is what every
 * interaction saved before it existed says and what a new one should go on saying. Switching the
 * step off keeps the block rather than deleting it, so the heading and the wording somebody wrote
 * are still there the next time they switch it back on. Both are the same thing to a reader: the
 * published document leaves a switched off signup out either way.
 */
function buildSignup(draft: SignupDraft): NewsletterSignup | undefined {
  const heading = trimmed(draft.heading);
  const buttonLabel = trimmed(draft.buttonLabel);
  const consentText = draft.consentText === null ? null : trimmed(draft.consentText);

  const saysSomething =
    draft.enabled ||
    heading !== '' ||
    buttonLabel !== '' ||
    consentText !== null ||
    draft.listId !== null;
  if (!saysSomething) return undefined;

  return {
    enabled: draft.enabled,
    ...(heading === '' ? {} : { heading }),
    ...(buttonLabel === '' ? {} : { button_label: buttonLabel }),
    ...(consentText === null ? {} : { consent_text: consentText }),
    ...(draft.listId === null ? {} : { list_id: draft.listId }),
  };
}

/**
 * Translate a schema complaint into something an editor can act on.
 *
 * The path says which field, and for the handful of places where one path carries several
 * different complaints (a choice question's options can be too few, or missing a correct answer,
 * or have too many correct answers) the schema's own message picks between them. Anything
 * unrecognised falls back to a general sentence rather than leaking the raw message.
 */
function friendlyMessage(format: Format, path: readonly PropertyKey[], message: string): string {
  const rules = FORMAT_RULES[format];
  const name = rules.label.toLowerCase();
  // A poll has one question, so naming it would only get in the way.
  const single = rules.maxQuestions === 1;
  const [first, second, third, fourth, fifth] = path;
  const questionNumber = typeof second === 'number' ? second + 1 : 1;
  const at = single ? '' : `Question ${String(questionNumber)}: `;
  const sentence = (text: string): string =>
    at === '' ? text.charAt(0).toUpperCase() + text.slice(1) : at + text;

  if (first === 'title') return `Give the ${name} a title.`;
  if (first === 'introduction') return 'The introduction is too long. Shorten it.';
  if (first === 'completion_message') return 'The finishing message is too long. Shorten it.';

  if (first === 'signup') {
    if (second === 'heading') return 'The newsletter heading is too long. Shorten it.';
    if (second === 'button_label') return 'The newsletter button label is too long. Shorten it.';
    if (second === 'consent_text') return 'The newsletter wording is too long. Shorten it.';
    if (second === 'list_id') return 'Pick the list again: the one selected no longer exists.';
    return 'Something in the newsletter signup is not valid. Check its heading and its wording.';
  }

  if (first === 'outcomes') {
    if (typeof second !== 'number') {
      return 'Add at least two outcomes for readers to land on.';
    }
    const outcome = `Outcome ${String(second + 1)}`;
    if (third === 'title') return `${outcome} needs a name.`;
    if (third === 'description') return `${outcome} has too long a description. Shorten it.`;
    if (third === 'image_url') {
      return `${outcome} needs a full image address starting with https, or none at all.`;
    }
    return `${outcome} is not finished. Check its name and description.`;
  }

  if (first === 'questions') {
    if (typeof second !== 'number') {
      if (message.includes('at most')) {
        return `A ${name} takes at most ${String(rules.maxQuestions)} questions.`;
      }
      return single ? `A ${name} needs one question.` : `Add at least one question.`;
    }

    if (third === 'prompt') {
      return single
        ? 'Write the question readers will answer.'
        : `Write what readers are asked in question ${String(questionNumber)}.`;
    }
    if (third === 'type') return sentence(`a ${name} cannot ask that kind of question.`);
    if (third === 'placeholder') return sentence('the placeholder text is too long. Shorten it.');
    if (third === 'multiline') {
      return sentence('only a question readers type into can be one line or a paragraph.');
    }
    if (third === 'scale') {
      if (message.includes('below')) {
        return sentence('the lowest number on the scale must be below the highest.');
      }
      return sentence('the scale needs a lowest and a highest whole number.');
    }

    if (third === 'options') {
      if (typeof fourth !== 'number') {
        if (message.includes('only have one correct')) {
          return sentence(
            'only one answer can be correct. Untick the others, or let readers pick more than one.',
          );
        }
        if (message.includes('correct answer')) {
          return sentence('tick the answer that is correct.');
        }
        return single
          ? `A ${name} needs between ${String(MIN_OPTIONS)} and ${String(MAX_OPTIONS)} options for readers to choose from.`
          : sentence(
              `it needs between ${String(MIN_OPTIONS)} and ${String(MAX_OPTIONS)} options for readers to choose from.`,
            );
      }

      const optionNumber = fourth + 1;
      if (fifth === 'outcome_weights') {
        return sentence(`choose which outcome option ${String(optionNumber)} points at.`);
      }
      return single
        ? `Option ${String(optionNumber)} needs some text, or remove it.`
        : sentence(`option ${String(optionNumber)} needs some text, or remove it.`);
    }
  }

  return message.length > 0 && message.length < 120
    ? `Check the ${name}: ` + message.toLowerCase()
    : `Something in this ${name} is not valid. Check the title, the questions and the answers.`;
}

/**
 * Change a question's type without losing what has been typed.
 *
 * Answer rows are kept even while a free text or scale question is selected, so switching by
 * accident and switching back costs nothing and keeps the option ids. Going from "pick any" to
 * "pick one" is the single case where something has to give: only one answer can be correct, so
 * the first ticked answer keeps its tick and the rest lose theirs, which is visible on screen
 * rather than turning up as an error on save.
 */
export function withQuestionType(
  question: QuestionDraft,
  type: QuestionType,
  format?: Format,
): QuestionDraft {
  let options = question.options;
  /**
   * The schema allows one line or a paragraph on a free text question and nowhere else, so a
   * leftover choice would refuse the save over a control no longer on the screen. Becoming a free
   * text question starts on the format's guess, which is the shape the widget would have rendered
   * anyway, so the control is filled in rather than blank without anything changing for a reader.
   */
  const multiline =
    type === 'free_text'
      ? (question.multiline ?? (format === undefined ? null : defaultMultiline(format, type)))
      : null;

  if (isChoice(type) && options.length < MIN_OPTIONS) {
    const added = [...options];
    while (added.length < MIN_OPTIONS) added.push(newOptionDraft());
    options = added;
  }

  if (type === 'single_choice') {
    let seen = false;
    options = options.map((option) => {
      if (!option.isCorrect) return option;
      if (seen) return { ...option, isCorrect: false };
      seen = true;
      return option;
    });
  }

  return { ...question, type, options, multiline };
}

/**
 * Remove an outcome, and every answer's weight pointing at it.
 *
 * Leaving the weights behind would fail the schema with a message about an outcome that is no
 * longer on the screen, which is not something an editor could act on.
 */
export function withOutcomeRemoved(draft: InteractionDraft, outcomeKey: string): InteractionDraft {
  return {
    ...draft,
    outcomes: draft.outcomes.filter((outcome) => outcome.key !== outcomeKey),
    questions: draft.questions.map((question) => ({
      ...question,
      options: question.options.map((option) => {
        if (!(outcomeKey in option.weights)) return option;
        const weights = { ...option.weights };
        delete weights[outcomeKey];
        return { ...option, weights };
      }),
    })),
  };
}
